import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { AttachmentsListComponent } from '@recruitee/components/attachments-list';
import { I18nService } from '@recruitee/i18n';
import type {
  InterviewResultAnswer,
  InterviewResultAnswerOption,
  RatingLevelTranslations,
} from '@recruitee/interview-results-types';
import { QuestionKind } from '@recruitee/interview-templates-types';
import {
  DividerComponent,
  InterviewEvaluationRatingComponent,
  BoxComponent,
  IconComponent,
  IconColorDirective,
  ItemComponent,
  ListComponent,
  HeadingComponent,
} from '@recruitee/user-interface';

@Component({
  selector: 'rt-interview-answer-list',
  templateUrl: 'interview-answer-list.component.html',
  styleUrls: ['./interview-answer-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    HeadingComponent,
    ListComponent,
    ItemComponent,
    IconColorDirective,
    IconComponent,
    BoxComponent,
    InterviewEvaluationRatingComponent,
    DividerComponent,
    AttachmentsListComponent,
  ],
})
export class InterviewAnswerListComponent {
  @Input() public answers: InterviewResultAnswer[];
  @Input() public canManageCandidates: boolean = false;
  @Input() public ratingLevelTranslations?: RatingLevelTranslations;

  public translations: {
    attachment: string;
  } = {
    attachment: this.i18n.translate('app.print.candidate_profile.attachment'),
  };

  private BooleanAnswerTranslationsMap: Record<string, string> = {
    yes: this.i18n.translate('app.common.boolean.yes'),
    no: this.i18n.translate('app.common.boolean.no'),
  };

  constructor(private i18n: I18nService) {}

  public isFile = (kind: QuestionKind) => QuestionKind.File === kind;

  public isSimple = (kind: QuestionKind) =>
    [
      QuestionKind.Dropdown,
      QuestionKind.SingleChoice,
      QuestionKind.String,
      QuestionKind.Text,
    ].includes(kind);

  public isMultiChoice = (kind: QuestionKind) => QuestionKind.MultipleChoice === kind;

  public isBoolean = (kind: QuestionKind) => QuestionKind.Boolean === kind;

  public hasAnswers(answer: InterviewResultAnswer): boolean {
    if (!answer) return false;

    return (
      !!(answer.content || '').length ||
      !!(answer.attachments || []).length ||
      this.hasAnswer(answer.contents || [])
    );
  }

  public isScorecard = (kind: QuestionKind) => QuestionKind.Scorecard === kind;

  public getBooleanAnswer = (answer: string) => this.BooleanAnswerTranslationsMap[answer];

  public filterEmptyOptions(answer: InterviewResultAnswer): InterviewResultAnswerOption[] {
    return (answer.options || []).filter((option, index) => answer.contents![index]);
  }

  public filterEmptyContents(contents: string[]): string[] {
    return contents.filter(c => c && c !== '');
  }

  public trackByAnswer(index: number, answer: InterviewResultAnswer): number {
    return answer.id;
  }

  public trackByContent(index: number, content: string): string {
    return content + index;
  }

  public trackByOption(index: number, option: InterviewResultAnswerOption): string {
    return option.title;
  }

  private hasAnswer(contents: string[]): boolean {
    return !!contents.filter(c => c && c !== '').length;
  }
}
